import { useState } from 'react';

import { useRouteAndQueryParams } from '@april9au/stack9-react';
import {
  S9ComponentConfigCategory,
  S9ComponentConfigRelease,
} from '@april9au/stack9-sdk';
import type { S9ComponentConfig } from '@april9au/stack9-sdk';
import { S9CustomEntityFormPage, S9PageSection } from '@april9au/stack9-ui';
import * as ui from '@april9au/stack9-ui';
import { Descriptions, Tag } from 'antd';
import dayjs from 'dayjs';

import { AppointmentSlotsSection } from '../components/AppointmentSlotsSection';
import { BookingsSection } from '../components/BookingsSection';
import { EventDrawer } from '../components/EventDrawer/EventDrawer';
import { AppRoutes } from '../constants/appRoutes';
import { useTabs } from '../hooks/useTabs';
import { DBEvent } from '../models/stack9/Event';
import { DBOrganisation } from '../models/stack9/Organisation';
import { DBTopic } from '../models/stack9/Topic';

type EventDetail = DBEvent & {
  department_id?: DBOrganisation[];
  department?: DBOrganisation[];
  topics?: DBTopic[];
  send_coordinator_email?: boolean | null;
  coordinator_email_template?: string | null;
  coordinator_email_subject?: string | null;
  send_reminder_email?: boolean | null;
  reminder_email_template?: string | null;
  reminder_email_subject?: string | null;
  reminder_email_hours_before?: number | null;
  send_reminder_sms?: boolean | null;
  reminder_sms_template?: string | null;
  reminder_sms_hours_before?: number | null;
  send_cancellation_email?: boolean | null;
  cancellation_email_template?: string | null;
  cancellation_email_subject?: string | null;
  send_cancellation_sms?: boolean | null;
  cancellation_sms_template?: string | null;
  send_cancellation_coordinator_email?: boolean | null;
  cancellation_coordinator_email_template?: string | null;
  cancellation_coordinator_email_subject?: string | null;
};

export const config: S9ComponentConfig = {
  name: 'EventDetailScreen',
  icon: '',
  category: S9ComponentConfigCategory.STACK9,
  version: S9ComponentConfigRelease.ALPHA,
  hasState: true,
  allowChildren: false,
  props: [],
};

const CustomEntityFormPage = S9CustomEntityFormPage.default;
const PageSection = S9PageSection.default;
const S9Tabs = ui.S9Tabs.default;
const S9Button = ui.S9Button.default;
const S9Skeleton = ui.Skeleton.default;

export const EventDetailScreen: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const {
    routeParams: { id = '' },
  } = useRouteAndQueryParams();
  const { currentTab, onChangeTab } = useTabs();

  const {
    data,
    isValidating: isLoading,
    mutate,
  } = ui.useScreenQueryById<EventDetail>('geteventbyid', +id);

  const formatDateTime = (
    date: string | Date | null | undefined,
    time?: string | null,
  ) => {
    if (!date) return '-';
    const dateStr = dayjs(date).format('DD/MM/YYYY');
    return time ? `${dateStr} ${time}` : dateStr;
  };

  const tabs = [
    {
      key: 'details',
      label: 'Event Details',
      children: isLoading ? (
        <S9Skeleton active />
      ) : (
        data && (
          <>
            <PageSection
              size="sm"
              title=" "
              actions={
                <>
                  <S9Button
                    label="Edit"
                    icon="EditOutlined"
                    type="link"
                    onClick={() => setIsDrawerOpen(true)}
                  />
                </>
              }
            >
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Event Name',
                    children: data.event_name,
                    span: 2,
                  },
                  {
                    label: 'Slug',
                    children: data.slug || '-',
                    span: 1,
                  },
                  {
                    label: 'Event Type',
                    children: data.event_type,
                    span: 1,
                  },
                  {
                    label: 'Booking Type',
                    children: data.booking_type,
                    span: 1,
                  },
                  {
                    label: 'Description',
                    children: data.event_description ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.event_description,
                        }}
                      />
                    ) : (
                      '-'
                    ),
                    span: 2,
                  },
                ]}
              />
            </PageSection>

            <PageSection size="sm" title="Date & Time">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Start Date',
                    children: formatDateTime(data.start_date, data.start_time),
                    span: 1,
                  },
                  {
                    label: 'End Date',
                    children: formatDateTime(data.end_date, data.end_time),
                    span: 1,
                  },
                  {
                    label: 'Timezone',
                    children: data.timezone || '-',
                    span: 1,
                  },
                  {
                    label: 'Publish Date',
                    children: data.publish_date
                      ? dayjs(data.publish_date).format('DD/MM/YYYY HH:mm')
                      : '-',
                    span: 1,
                  },
                  {
                    label: 'Retire Date',
                    children: data.retire_date
                      ? dayjs(data.retire_date).format('DD/MM/YYYY HH:mm')
                      : '-',
                    span: 1,
                  },
                  ...(data.booking_type === 'Register' &&
                  data.registration_cutoff_date
                    ? [
                        {
                          label: 'Registration Cutoff Date',
                          children: dayjs(data.registration_cutoff_date).format(
                            'DD/MM/YYYY HH:mm',
                          ),
                          span: 1,
                        },
                      ]
                    : []),
                  ...(data.booking_type === 'Appointment' &&
                  data.appointment_cutoff_date
                    ? [
                        {
                          label: 'Appointment Cutoff Date',
                          children: dayjs(data.appointment_cutoff_date).format(
                            'DD/MM/YYYY HH:mm',
                          ),
                          span: 1,
                        },
                      ]
                    : []),
                ]}
              />
            </PageSection>

            <PageSection size="sm" title="Location & Contact">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Location',
                    children: data.location || '-',
                    span: 1,
                  },
                  {
                    label: 'Region',
                    children: data.region || '-',
                    span: 1,
                  },
                  {
                    label: 'Event Coordinator',
                    children: data.event_coordinator || '-',
                    span: 1,
                  },
                  {
                    label: 'Coordinator Email',
                    children: data.coordinator_email || '-',
                    span: 1,
                  },
                ]}
              />
            </PageSection>

            {data.booking_type === 'Appointment' && (
              <PageSection size="sm" title="Appointment Settings">
                <Descriptions
                  bordered
                  column={3}
                  items={[
                    {
                      label: 'Slot Duration (minutes)',
                      children: data.slot_duration_minutes || '-',
                      span: 1,
                    },
                    {
                      label: 'Slot Capacity',
                      children: data.slot_capacity || '-',
                      span: 1,
                    },
                    {
                      label: 'Max Capacity',
                      children: data.max_capacity || '-',
                      span: 1,
                    },
                    {
                      label: 'Total Bookings',
                      children: data.total_bookings || '0',
                      span: 1,
                    },
                    {
                      label: 'Available Spaces',
                      children: data.available_spaces || '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            {data.booking_type === 'Register' && (
              <PageSection size="sm" title="Registration Settings">
                <Descriptions
                  bordered
                  column={3}
                  items={[
                    {
                      label: 'Max Capacity',
                      children: data.max_capacity || '-',
                      span: 1,
                    },
                    {
                      label: 'Total Bookings',
                      children: data.total_bookings || '0',
                      span: 1,
                    },
                    {
                      label: 'Available Spaces',
                      children: data.available_spaces || '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            {data.booking_type === 'Webinar' && (
              <PageSection size="sm" title="Webinar Settings">
                <Descriptions
                  bordered
                  column={1}
                  items={[
                    {
                      label: 'Webinar URL',
                      children: data.webinar_url ? (
                        <a
                          href={data.webinar_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data.webinar_url}
                        </a>
                      ) : (
                        '-'
                      ),
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            {(data.booking_type === 'Register' ||
              data.booking_type === 'Appointment') && (
              <PageSection size="sm" title="Notifications">
                <Descriptions
                  bordered
                  column={3}
                  items={[
                    ...(data.booking_type === 'Register'
                      ? [
                          {
                            label: 'Send Registration Email',
                            children: data.send_registration_email ? (
                              <Tag color="green">Yes</Tag>
                            ) : (
                              <Tag>No</Tag>
                            ),
                            span: 1,
                          },
                          {
                            label: 'Registration Email Template',
                            children: data.registration_email_template || '-',
                            span: 1,
                          },
                          {
                            label: 'Registration Email Subject',
                            children: data.registration_email_subject || '-',
                            span: 1,
                          },
                          {
                            label: 'Send Registration SMS',
                            children: data.send_registration_sms ? (
                              <Tag color="green">Yes</Tag>
                            ) : (
                              <Tag>No</Tag>
                            ),
                            span: 1,
                          },
                          {
                            label: 'Registration SMS Template',
                            children: data.registration_sms_template || '-',
                            span: 2,
                          },
                        ]
                      : []),
                    ...(data.booking_type === 'Appointment'
                      ? [
                          {
                            label: 'Send Appointment Email',
                            children: data.send_appointment_email ? (
                              <Tag color="green">Yes</Tag>
                            ) : (
                              <Tag>No</Tag>
                            ),
                            span: 1,
                          },
                          {
                            label: 'Appointment Email Template',
                            children: data.appointment_email_template || '-',
                            span: 1,
                          },
                          {
                            label: 'Appointment Email Subject',
                            children: data.appointment_email_subject || '-',
                            span: 1,
                          },
                          {
                            label: 'Send Appointment SMS',
                            children: data.send_appointment_sms ? (
                              <Tag color="green">Yes</Tag>
                            ) : (
                              <Tag>No</Tag>
                            ),
                            span: 1,
                          },
                          {
                            label: 'Appointment SMS Template',
                            children: data.appointment_sms_template || '-',
                            span: 2,
                          },
                        ]
                      : []),
                    {
                      label: 'Send Coordinator Email',
                      children: data.send_coordinator_email ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Coordinator Email Template',
                      children: data.coordinator_email_template || '-',
                      span: 1,
                    },
                    {
                      label: 'Coordinator Email Subject',
                      children: data.coordinator_email_subject || '-',
                      span: 1,
                    },
                    {
                      label: 'Send Reminder Email',
                      children: data.send_reminder_email ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Reminder Email Template',
                      children: data.reminder_email_template || '-',
                      span: 1,
                    },
                    {
                      label: 'Reminder Email Subject',
                      children: data.reminder_email_subject || '-',
                      span: 1,
                    },
                    {
                      label: 'Reminder Email Hours Before',
                      children: data.reminder_email_hours_before || '-',
                      span: 3,
                    },
                    {
                      label: 'Send Reminder SMS',
                      children: data.send_reminder_sms ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Reminder SMS Template',
                      children: data.reminder_sms_template || '-',
                      span: 1,
                    },
                    {
                      label: 'Reminder SMS Hours Before',
                      children: data.reminder_sms_hours_before || '-',
                      span: 1,
                    },
                    {
                      label: 'Send Cancellation Email',
                      children: data.send_cancellation_email ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Cancellation Email Template',
                      children: data.cancellation_email_template || '-',
                      span: 1,
                    },
                    {
                      label: 'Cancellation Email Subject',
                      children: data.cancellation_email_subject || '-',
                      span: 1,
                    },
                    {
                      label: 'Send Cancellation SMS',
                      children: data.send_cancellation_sms ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Cancellation SMS Template',
                      children: data.cancellation_sms_template || '-',
                      span: 2,
                    },
                    {
                      label: 'Send Cancellation Coordinator Email',
                      children: data.send_cancellation_coordinator_email ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Cancellation Coordinator Email Template',
                      children:
                        data.cancellation_coordinator_email_template || '-',
                      span: 1,
                    },
                    {
                      label: 'Cancellation Coordinator Email Subject',
                      children:
                        data.cancellation_coordinator_email_subject || '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            {(data.booking_type === 'Register' ||
              data.booking_type === 'Appointment') && (
              <PageSection size="sm" title="Consent & Waiver">
                <Descriptions
                  bordered
                  column={2}
                  items={[
                    {
                      label: 'Requires Consent',
                      children: data.requires_consent ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Consent Text',
                      children: data.consent_text || '-',
                      span: 1,
                    },
                    {
                      label: 'Requires Waiver',
                      children: data.requires_waiver ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag>No</Tag>
                      ),
                      span: 1,
                    },
                    {
                      label: 'Waiver Text',
                      children: data.waiver_text || '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            <PageSection size="sm" title="Organization & Display">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Department',
                    children:
                      data.department && data.department.length > 0
                        ? data.department.map(dept => dept.name).join(', ')
                        : '-',
                    span: 1,
                  },
                  {
                    label: 'Topics',
                    children:
                      data.topics && data.topics.length > 0
                        ? data.topics.map(topic => (
                            <Tag key={topic.id}>{topic.name}</Tag>
                          ))
                        : '-',
                    span: 1,
                  },
                  {
                    label: 'Featured',
                    children: data.is_featured ? (
                      <Tag color="blue">Yes</Tag>
                    ) : (
                      <Tag>No</Tag>
                    ),
                    span: 1,
                  },
                  {
                    label: 'Featured Order',
                    children: data.featured_order || '-',
                    span: 1,
                  },
                  {
                    label: 'Event Thumbnail',
                    children:
                      data.event_thumbnail &&
                      Array.isArray(data.event_thumbnail) &&
                      data.event_thumbnail.length > 0 &&
                      data.event_thumbnail[0]?.url ? (
                        <img
                          src={data.event_thumbnail[0].url}
                          alt="Event thumbnail"
                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                        />
                      ) : (
                        '-'
                      ),
                    span: 2,
                  },
                ]}
              />
            </PageSection>
          </>
        )
      ),
    },
    {
      key: 'bookings',
      label: 'Bookings',
      children: (
        <BookingsSection
          eventId={id ? +id : undefined}
          timezone={data?.timezone}
        />
      ),
    },
    ...(data?.booking_type === 'Appointment'
      ? [
          {
            key: 'appointment_slots',
            label: 'Appointment Slots',
            children: (
              <AppointmentSlotsSection
                eventId={id ? +id : undefined}
                bookingType={data?.booking_type}
                timezone={data?.timezone}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <CustomEntityFormPage
      entityKey="event"
      entityId={+id}
      title={`Event #${id}`}
      cancelLink={AppRoutes.EventList}
      screenQueryName="geteventbyid"
    >
      <S9Tabs
        defaultActiveKey={currentTab || 'details'}
        onChange={onChangeTab}
        items={tabs}
      />
      <EventDrawer
        open={isDrawerOpen}
        id={data?.id}
        onSaved={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onDeleted={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      />
    </CustomEntityFormPage>
  );
};
